'use client'
import React, { ReactElement } from 'react'

import { clsx } from 'clsx'
import { useAtomValue } from 'jotai'

import { mapRoute } from '@dfds-pax/utils'

import { selectedRoutesAtom } from '@/states/selectionAtom'
import ArrowForwardIcon from '@/ui-cvi/components/dxp-icons/system/ArrowForwardIcon'

import { extraShortDateFormat, longDateFormat } from '../../utils'

import { useTranslation } from '@/hooks/useDebugTranslations'

import PriceItem from './PriceItem'

interface LegInfoProps {
  routes?: { outbound: string; homebound: string | null }
  arrivalTime: string | null
  checkinTime: string | null
  currencyCode: string
  departureDate: string
  departureTime: string | null
  expandCollapseClicked?: () => void
  hideOfferPrice?: boolean
  offerPrice: number | undefined
  productCode: string
  ticketPrice: number
  type: 'outbound' | 'return'
}

const SummaryLeg = ({
  arrivalTime,
  // checkinTime,
  currencyCode,
  departureDate,
  departureTime,
  hideOfferPrice,
  offerPrice,
  productCode,
  routes,
  ticketPrice,
  type,
}: LegInfoProps): ReactElement | null => {
  const storedRoutes = useAtomValue(selectedRoutesAtom)
  const selectedRoutes = routes ?? storedRoutes
  const outboundUnloRoute = selectedRoutes.outbound ? mapRoute(selectedRoutes.outbound) : undefined
  const homeboundUnloRoute = selectedRoutes.homebound ? mapRoute(selectedRoutes.homebound) : undefined

  const { t } = useTranslation()
  return (
    <div className="flex flex-col flex-nowrap items-stretch" data-e2e="bookingSummaryLegInfo" data-e2e-summaryleg={type.toLowerCase()}>
      <div className={clsx('pb-semantic-global-xs flex flex-col justify-center')}>
        <div className="typography-body-md-medium text-semantic-global-text-default flex flex-row flex-wrap gap-x-2 text-left">
          <span
            data-e2e="bookingSummaryRouteInfo"
            data-e2e-date={extraShortDateFormat(departureDate)}
            data-e2e-datetime={`${departureDate}/${departureTime}`.replace(/[-:]/g, '')}
            data-e2e-time={departureTime}
          >
            <span>{type === 'outbound' ? t('OUTBOUND') : t('RETURN')}: </span>
            <span>{longDateFormat(departureDate)}</span>
          </span>
        </div>
      </div>

      {!departureTime ? (
        <div className="typography-body-md-regular text-semantic-global-text-default text-left">
          {type === 'outbound' ? t('MISSING-OUTBOUND-DEPARTURE') : t('MISSING-RETURN-DEPARTURE')}
        </div>
      ) : null}

      {departureTime && ticketPrice ? (
        <div className="gap-semantic-global-xs flex flex-col">
          <div className="gap-semantic-global-2xs typography-body-md-regular text-semantic-global-text-default flex flex-col">
            <div className="flex flex-row items-center text-left">
              <span data-e2e="booking-summary-outbound-departure">
                {outboundUnloRoute
                  ? t(`${type === 'outbound' ? outboundUnloRoute.departureUnLoCode : homeboundUnloRoute?.departureUnLoCode}`)
                  : null}
              </span>
              <span className="mx-primitive-8">
                <ArrowForwardIcon />
              </span>
              <span data-e2e="booking-summary-return-departure">
                {outboundUnloRoute
                  ? t(`${type === 'outbound' ? outboundUnloRoute.arrivalUnLoCode : homeboundUnloRoute?.arrivalUnLoCode}`)
                  : null}{' '}
                {t('AT')} <span data-e2e="bookingSummaryDepartureTime">{departureTime}</span>
              </span>
            </div>
            <div className="flex flex-row items-center text-left">
              <span>
                {t('ARRIVAL-TIME')} {arrivalTime}
              </span>
            </div>
          </div>
          <PriceItem
            currency={currencyCode}
            description={t(`${productCode}-TICKET`)}
            endToEndDescrAttr="bookingSummaryTicketType"
            endToEndPriceAttr="bookingSummaryTicketPrice"
            hideOfferPrice={hideOfferPrice}
            offerPrice={offerPrice}
            price={ticketPrice}
          />
        </div>
      ) : null}
    </div>
  )
}

export default SummaryLeg
